if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // `debounce` is defined globally in assets/global.js, which loads before this file.
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if ((this.dataset.desktopLayout.includes('thumbnail') || this.dataset.desktopLayout === 'slideshow') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        const host = this.elements.thumbnails;
        if (!host.slider || typeof host.isSlideVisible !== 'function') return;
        if (host.isSlideVisible(thumbnail, 10)) return;

        host.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || position === null || position === undefined) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      connectedCallback() {
        this.attachMobileFullSwipeSnap();
      }

      disconnectedCallback() {
        if (this._swipeList) {
          this._swipeList.removeEventListener('touchstart', this._onSwipeStart);
          this._swipeList.removeEventListener('touchmove', this._onSwipeMove);
          this._swipeList.removeEventListener('touchend', this._onSwipeEnd);
          this._swipeList.removeEventListener('touchcancel', this._onSwipeEnd);
          this._swipeList = null;
        }
      }

      /**
       * Mobile product gallery: full-snap swipe only, with early directional lock.
       * - No partial drag / no peek of next image mid-gesture (CSS pins list to pan-y).
       * - Direction is decided by the FIRST meaningful movement:
       *     - Any vertical component >= 6px OR vertical >= horizontal in the early
       *       sample → locked as 'vertical', the swipe is dead for the rest of the
       *       gesture (so page scroll is never misread as a slide change).
       *     - Horizontal component >= 8px AND > 1.5× vertical → locked as 'horizontal'.
       * - Only a 'horizontal'-locked gesture with |dx| past threshold advances a slide.
       * - This mirrors the hero slideshow intent: left/right only, no previews, no half swipes.
       */
      attachMobileFullSwipeSnap() {
        if (this.classList.contains('product__media-gallery--scroll')) return;
        const list = this.querySelector('.product__media-gallery-main .product__media-list');
        if (!list || list.dataset.fullSwipeBound === 'true') return;
        list.dataset.fullSwipeBound = 'true';

        const mql = window.matchMedia('(max-width: 749px)');
        const H_COMMIT_PX = 60;   // must drag this far horizontally to commit a slide change
        const H_LOCK_PX = 8;      // horizontal distance at which we lock the direction
        const V_LOCK_PX = 6;      // any vertical drift this small kills the swipe
        const H_DOMINANCE = 1.5;  // horizontal must clearly beat vertical to count as a swipe

        let startX = 0;
        let startY = 0;
        let startT = 0;
        let active = false;
        let lockedDir = 'undetermined'; // 'undetermined' | 'horizontal' | 'vertical'

        this._onSwipeStart = (e) => {
          if (!mql.matches) { active = false; return; }
          if (!e.touches || e.touches.length !== 1) { active = false; return; }
          const t = e.touches[0];
          startX = t.clientX;
          startY = t.clientY;
          startT = Date.now();
          active = true;
          lockedDir = 'undetermined';
        };

        this._onSwipeMove = (e) => {
          if (!active || !e.touches || e.touches.length !== 1) return;
          if (lockedDir !== 'undetermined') return;
          const t = e.touches[0];
          const dx = t.clientX - startX;
          const dy = t.clientY - startY;
          const adx = Math.abs(dx);
          const ady = Math.abs(dy);

          // Vertical intent wins early — any real vertical drift, or vertical >= horizontal,
          // locks this gesture out of the swipe handler. This is the main fix: a user
          // scrolling the page vertically will never accidentally flip to the next image.
          if (ady >= V_LOCK_PX && ady >= adx) {
            lockedDir = 'vertical';
            active = false;
            return;
          }
          // Horizontal lock requires clear dominance over any vertical drift.
          if (adx >= H_LOCK_PX && adx > ady * H_DOMINANCE) {
            lockedDir = 'horizontal';
          }
        };

        this._onSwipeEnd = (e) => {
          if (!active) return;
          active = false;
          // If we never committed to a horizontal lock, this wasn't a swipe — do nothing.
          if (lockedDir !== 'horizontal') return;
          const touch = (e.changedTouches && e.changedTouches[0]) || null;
          if (!touch) return;
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          const dt = Date.now() - startT;
          // Final safety checks: horizontal must still dominate, and cross the threshold
          // (or be a fast flick >= 35px under 300ms).
          if (Math.abs(dy) > Math.abs(dx)) return;
          const flick = Math.abs(dx) >= 35 && dt < 300;
          if (!flick && Math.abs(dx) < H_COMMIT_PX) return;

          // Find current slide (the one closest to list.scrollLeft) and step by one.
          const slides = Array.from(list.querySelectorAll('.product__media-item, .slider__slide'));
          if (!slides.length) return;
          const scrollLeft = list.scrollLeft;
          let currentIdx = 0;
          let bestDelta = Infinity;
          slides.forEach((s, i) => {
            const d = Math.abs(s.offsetLeft - scrollLeft);
            if (d < bestDelta) { bestDelta = d; currentIdx = i; }
          });
          const dir = dx < 0 ? 1 : -1; // swipe left → next, swipe right → prev
          const targetIdx = Math.max(0, Math.min(slides.length - 1, currentIdx + dir));
          if (targetIdx === currentIdx) return;
          const target = slides[targetIdx];
          list.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });

          // Keep the media-gallery's internal active-media state in sync so
          // thumbnail highlighting / accessibility announcements follow the swipe.
          const mediaId = target.dataset.mediaId;
          if (mediaId && typeof this.setActiveMedia === 'function') {
            try { this.setActiveMedia(mediaId, false); } catch (_) {}
          }
        };

        list.addEventListener('touchstart', this._onSwipeStart, { passive: true });
        list.addEventListener('touchmove', this._onSwipeMove, { passive: true });
        list.addEventListener('touchend', this._onSwipeEnd, { passive: true });
        list.addEventListener('touchcancel', this._onSwipeEnd, { passive: true });
        this._swipeList = list;
      }

    }
  );
}
